import api from './api';

export type SubscriptionStatus = 'trial' | 'active' | 'suspended' | 'cancelled';
export type PaymentStatus = 'paid' | 'pending' | 'overdue';

export interface Subscription {
  id: number;
  tenant_id: number;
  plan: string;
  status: SubscriptionStatus;
  price_monthly: number;
  start_date: string | null;
  end_date: string | null;
  payment_status: PaymentStatus;
  last_payment_date: string | null;
  next_payment_date: string | null;
  notes: string | null;
  // Reporte manual de pago
  payment_reported_at: string | null;
  payment_reported_reference: string | null;
  // IPC (Fase 3)
  auto_ipc_update: boolean;
  last_ipc_adjustment_at: string | null;
  // Mercado Pago
  mp_preapproval_id: string | null;
  mp_init_point: string | null;
  mp_status: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface BillingInfo {
  alias: string | null;
  cbu: string | null;
  account_holder: string | null;
  bank_name: string | null;
  whatsapp_number: string | null;
  payment_instructions: string | null;
}

export interface BillingSettings extends BillingInfo {
  id: number;
  cuit: string | null;
}

export interface FeaturesConfig {
  mp_enabled: boolean;
}

export interface ActivateResponse {
  init_point: string;
  preapproval_id: string;
}

export const subscriptionsService = {
  // Tenant admin
  async getMine(): Promise<Subscription> {
    const response = await api.get<Subscription>('/subscriptions/me');
    return response.data;
  },

  async getBillingInfo(): Promise<BillingInfo> {
    const response = await api.get<BillingInfo>('/subscriptions/billing-info');
    return response.data;
  },

  async reportPayment(reference?: string): Promise<Subscription> {
    const response = await api.post<Subscription>('/subscriptions/report-payment', { reference });
    return response.data;
  },

  // MP (oculto detras del feature flag)
  async activate(): Promise<ActivateResponse> {
    const response = await api.post<ActivateResponse>('/subscriptions/activate');
    return response.data;
  },

  async cancel(): Promise<Subscription> {
    const response = await api.post<Subscription>('/subscriptions/cancel');
    return response.data;
  },

  // Superadmin
  async updatePrice(
    subscriptionId: number,
    data: { new_price: number; reason?: string }
  ): Promise<Subscription> {
    const response = await api.patch<Subscription>(`/admin/subscriptions/${subscriptionId}/price`, data);
    return response.data;
  },

  async confirmPayment(subscriptionId: number, notes?: string): Promise<Subscription> {
    const response = await api.post<Subscription>(`/admin/subscriptions/${subscriptionId}/confirm-payment`, { notes });
    return response.data;
  },

  async listPendingPayments(): Promise<Subscription[]> {
    const response = await api.get<Subscription[]>('/admin/subscriptions/pending-payments');
    return response.data;
  },

  // Billing settings (superadmin)
  async getBillingSettings(): Promise<BillingSettings> {
    const response = await api.get<BillingSettings>('/admin/billing-settings');
    return response.data;
  },

  async updateBillingSettings(data: Partial<BillingSettings>): Promise<BillingSettings> {
    const response = await api.put<BillingSettings>('/admin/billing-settings', data);
    return response.data;
  },
};

export const featuresService = {
  async get(): Promise<FeaturesConfig> {
    const response = await api.get<FeaturesConfig>('/features');
    return response.data;
  },
};
